import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Provider } from "react-redux";
import { LazyMotion, MotionConfig } from "framer-motion";
import { store } from "./app/store";
import "./index.css";
import "./styles/themes.css";
import "./i18n";
import { LoadingProvider } from "./context/LoadingContext";
import { PreviewProvider } from "./components/preview/PreviewContext";
import { GlobalPreviewModal } from "./components/preview/GlobalPreviewModal";
import { GlobalTooltips } from "./components/common/GlobalTooltips";
import { OrgDateProvider } from "./utils/date/OrgDateProvider";
import { MemberCardProvider } from "./components/member/MemberCardContext";
import { GlobalMemberCard } from "./components/member/GlobalMemberCard";
import App from "./app/App";
import { preloadOpeningRoute } from "./app/routes";
import { AuthProvider } from "./auth/AuthProvider";
import { OrganisationSwitchProvider } from "./auth/OrganisationSwitchProvider";
import { LicensingProvider } from "./auth/LicensingProvider";

// A tab opened before a deploy still asks for the old build's chunk names,
// which the server no longer has (a 404 under /assets/). Reload once to pick
// up the new index.html and its chunks, instead of landing on the error
// page. At most one such reload a minute, so a chunk genuinely missing from
// the new build too surfaces as an error rather than a reload loop.
window.addEventListener("vite:preloadError", (event) => {
  const key = "lawsigna:chunk-reload-at";
  try {
    const last = Number(sessionStorage.getItem(key) || 0);
    if (Date.now() - last < 60_000) return;
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    // Without storage a reload could repeat; let the error surface instead.
    return;
  }
  event.preventDefault();
  window.location.reload();
});

const queryClient = new QueryClient();

// Animation features load in their own chunk; `m.*` elements render
// straight away and start animating once it arrives.
const loadMotionFeatures = () =>
  import("./utils/motionFeatures").then((mod) => mod.default);

// Signed in already: fetch the page being opened alongside /Auth/me rather
// than after it (see preloadOpeningRoute).
try {
  if (localStorage.getItem("accessToken")) {
    preloadOpeningRoute(window.location.pathname);
  }
} catch {
  // No storage: the page still loads, just not ahead of time.
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <LazyMotion features={loadMotionFeatures}>
    {/* "user": with the OS reduce-motion setting on, framer-motion drops
        transform/layout animation (page slides, springs) and keeps the
        opacity fades, so state changes still read. */}
    <MotionConfig reducedMotion="user">
    <LoadingProvider>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <PreviewProvider>
            <AuthProvider>
              <OrganisationSwitchProvider>
                <LicensingProvider>
                  <OrgDateProvider>
                    <MemberCardProvider>
                      <App />
                      <GlobalPreviewModal />
                      <GlobalTooltips />
                      <GlobalMemberCard />
                    </MemberCardProvider>
                  </OrgDateProvider>
                </LicensingProvider>
              </OrganisationSwitchProvider>
            </AuthProvider>
          </PreviewProvider>
        </QueryClientProvider>
      </Provider>
    </LoadingProvider>
    </MotionConfig>
  </LazyMotion>,
);
